import { AlertType, Season, SkatingPass } from '@types';
import { AlertModal, Card, LoadingSpinner } from '_atoms';
import { SeasonDetailsService, SkatingPassService } from '_services';
import React, { useCallback, useEffect, useState } from 'react';
import { MdCheckCircle, MdInfo } from 'react-icons/md';

interface SelectSkatingPassProps {
  selectedPass?: SkatingPass;
  selectedPassType?: 'adult' | 'child';
  onNext: (data?: any) => void;
  onStepComplete: (data: any) => void;
  isPreview?: boolean;
}

// Helper function to create a unique key for each option
const getOptionKey = (pass: SkatingPass, type: 'adult' | 'child') =>
  `${pass.id}-${type}`;

export const SelectSkatingPass: React.FC<SelectSkatingPassProps> = ({
  selectedPass,
  selectedPassType,
  onNext,
  onStepComplete,
  isPreview = false,
}) => {
  const [passes, setPasses] = useState<SkatingPass[]>([]);
  const [selectedOptionKey, setSelectedOptionKey] = useState<
    string | undefined
  >(
    selectedPass && selectedPassType
      ? getOptionKey(selectedPass, selectedPassType)
      : undefined,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [settings, setSettings] = useState<Season>({
    id: '',
    startDate: '',
    endDate: '',
    isTicketPurchasesEnabled: false,
    isSeasonPassPurchasesEnabled: false,
    isPartyPackagePurchasesEnabled: false,
    isSkatingLessonPurchasesEnabled: false,
    isHockeyLessonPurchasesEnabled: false,
    sessionTimes: [],
  });
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: AlertType;
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const showAlert = useCallback(
    (type: AlertType, title: string, message: string) => {
      setAlertModal({
        isOpen: true,
        type,
        title,
        message,
      });
    },
    [],
  );

  const loadSkatingPasses = useCallback(async () => {
    setIsLoading(true);
    try {
      const responseData = await SkatingPassService.getSkatingPasses();
      if (responseData && Array.isArray(responseData)) {
        setPasses(responseData.filter(pass => pass.isActive));
      } else {
        setPasses([]);
      }
    } catch (error) {
      console.error('Error loading skating passes:', error);
      showAlert('error', 'Error', 'Failed to load skating passes.');
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    loadCurrentSeason();
    loadSkatingPasses();
  }, [loadSkatingPasses]);

  const loadCurrentSeason = async () => {
    setIsLoadingInitial(true);
    try {
      const responseData =
        (await SeasonDetailsService.getCurrentSeasonDetails()) as unknown as Season;

      if (responseData) {
        setSettings(responseData);
      }
    } catch (error) {
      console.error('Error loading current season:', error);
    } finally {
      setIsLoadingInitial(false);
    }
  };

  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, isOpen: false }));
  };

  const handlePassSelect = (pass: SkatingPass, type: 'adult' | 'child') => {
    if (isPreview) {
      return;
    }
    const optionKey = getOptionKey(pass, type);
    setSelectedOptionKey(optionKey);
  };

  const handleContinue = () => {
    if (isPreview) {
      return;
    }
    if (selectedOptionKey) {
      // Find the pass and type from the selected key
      const [passId, type] = selectedOptionKey.split('-');
      const pass = passes.find(p => parseInt(p.id) === parseInt(passId));

      if (pass && (type === 'adult' || type === 'child')) {
        const selectedPrice =
          type === 'adult' ? pass.adultPrice : pass.childPrice;

        onStepComplete({
          selectedPass: pass,
          selectedPassType: type,
          selectedPrice,
        });
        onNext({
          selectedPass: pass,
          selectedPassType: type,
          selectedPrice,
        });
      } else {
        showAlert(
          'error',
          'Selection Error',
          'Unable to process your selection. Please try again.',
        );
      }
    } else {
      showAlert(
        'error',
        'Selection Required',
        'Please select a pass type to continue.',
      );
    }
  };

  const NotInSeason = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <Card className="w-full max-w-md justify-center items-center border-none">
        <div className="text-center space-y-4">
          <MdInfo className="mx-auto text-gray-400 text-6xl" />
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Season Pass Purchases Not Available
            </h3>
            <p className="text-gray-600">
              Season pass purchases are currently not enabled. Please check back
              later or contact us for more information.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {!settings.isSeasonPassPurchasesEnabled && !isLoadingInitial ? (
        <NotInSeason />
      ) : (
        <>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Select Your Skating Pass
            </h2>
            <p className="text-gray-600">
              Choose the pass that best fits your skating needs
            </p>
          </div>

          {passes.length === 0 ? (
            <div className="text-center py-12">
              <MdInfo className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No passes available
              </h3>
              <p className="text-gray-500">
                There are currently no skating passes available for booking.
              </p>
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-2">
              {passes.map(pass => (
                <div
                  key={pass.id}
                  className="border border-gray-200 rounded-lg p-6 bg-white shadow-sm"
                >
                  <img
                    src={pass.photoUrl}
                    alt={pass.name}
                    className="w-full h-48 object-cover rounded-lg mb-6"
                  />
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {pass.name}
                    </h3>
                    <p
                      className="text-gray-600 text-sm mb-4 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_p]:mb-2"
                      dangerouslySetInnerHTML={{ __html: pass.description }}
                    />

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Max Uses:</span>
                        <span className="font-semibold text-gray-900">
                          {pass.maxUses === null || pass.maxUses === 0
                            ? 'Unlimited'
                            : `${pass.maxUses} sessions`}
                        </span>
                      </div>
                    </div>

                    {pass.disclaimer && (
                      <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                        <p className="text-xs text-yellow-800">
                          {pass.disclaimer}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <button
                      onClick={() => handlePassSelect(pass, 'adult')}
                      disabled={isPreview}
                      className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                        isPreview
                          ? 'bg-gray-100 text-gray-500 border-2 border-gray-200 cursor-not-allowed'
                          : selectedOptionKey === getOptionKey(pass, 'adult')
                            ? 'bg-blue-600 text-white border-2 border-blue-600'
                            : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        {!isPreview &&
                          selectedOptionKey === getOptionKey(pass, 'adult') && (
                            <MdCheckCircle className="w-5 h-5" />
                          )}
                        <span>
                          {isPreview
                            ? 'Adult Pass Price'
                            : `PURCHASE ${pass.name.toUpperCase()} - ADULT`}
                        </span>
                      </div>
                      <div className="text-lg font-bold mt-1">
                        ${pass.adultPrice}
                      </div>
                    </button>

                    <button
                      onClick={() => handlePassSelect(pass, 'child')}
                      disabled={isPreview}
                      className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                        isPreview
                          ? 'bg-gray-100 text-gray-500 border-2 border-gray-200 cursor-not-allowed'
                          : selectedOptionKey === getOptionKey(pass, 'child')
                            ? 'bg-blue-600 text-white border-2 border-blue-600'
                            : 'bg-white text-gray-700 border-2 border-gray-300 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <div className="flex items-center justify-center space-x-2">
                        {!isPreview &&
                          selectedOptionKey === getOptionKey(pass, 'child') && (
                            <MdCheckCircle className="w-5 h-5" />
                          )}
                        <span>
                          {isPreview
                            ? 'Child Pass Price'
                            : `PURCHASE ${pass.name.toUpperCase()} - CHILD`}
                        </span>
                      </div>
                      <div className="text-lg font-bold mt-1">
                        ${pass.childPrice}
                      </div>
                    </button>
                  </div>
                  {isPreview && (
                    <p className="text-xs text-gray-500 mt-3 text-center">
                      Sign in to purchase this pass.
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {!isPreview && (
            <div className="flex justify-end pt-6">
              <button
                onClick={handleContinue}
                disabled={!selectedOptionKey}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  selectedOptionKey
                    ? 'bg-blue-600 text-white hover:bg-blue-700'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Continue
              </button>
            </div>
          )}
        </>
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
        onClose={closeAlert}
      />
    </div>
  );
};
